import { REPORT } from '@consts/reports';
import { useReportLoad } from '@hooks/useReportLoad';
import { BaseReport } from '../BaseReport';
import { average, countUnique } from '@utils/summaries';
import { getUniqueDates, groupByKeys, seriesMap, groupBy } from '@utils/data';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';

interface WarrantyRatePctByResellerProps {
    dateRange: { startDate: string; endDate: string };
}

export const WarrantyRatePctByReseller = ({ dateRange }: WarrantyRatePctByResellerProps) => {
    const [range, setRange] = useRange('WarrantyRatePctByReseller');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.RESELLER_WARRANTY_RATE_BY_RESELLER_COMPARED_TO_GLOBAL_AVERAGE,
        params: { ...dateRange, range },
    });

    const warrantyAvg = groupBy(data, 'date');

    const resellers = groupByKeys(data, ['resellerName', 'date']);

    const dateKeys = getUniqueDates(data, range);

    const globalAvgSeries = {
        visible: true,
        name: 'Global Warranty Avg',
        data: dateKeys.map(({ date }) => warrantyAvg[date]?.[0]?.globalWarrantyAvg || 0),
    };

    const resellerSeries = seriesMap({
        items: resellers,
        valueKey: 'companyWarrantyAvg',
        visible: false,
        dateKeys,
    });

    return (
        <BaseReport
            title={'Warranty Rate by Reseller'}
            description={
                'This graph shows you the warranty rate of each of your Resellers (warranty cuts divided by total cuts), and allows you to compare them to the global average.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            range={range}
            setRange={setRange}
            tableOptions={{
                headers: ['Reseller Name', 'Date', 'Company Warranty Avg', 'Global Warranty Avg'],
                data: data.map((item) => [
                    item.resellerName,
                    item.date,
                    item.companyWarrantyAvg,
                    item.globalWarrantyAvg,
                ]),
                summaries: [countUnique, countUnique, average, average],
                style: [null, styleDate(range)],
                sort: [basicSort, dateSort(range), basicSort, basicSort],
            }}
            chartOptions={chart(500, [{ name: 'Company Warranty Average', opposite: false }], dateKeys, [
                globalAvgSeries,
                ...resellerSeries,
            ])}
        />
    );
};
